import { useState } from 'react'
import { inject, observer } from 'mobx-react'
import useMediaQuery from '@mui/material/useMediaQuery'

import { LoadingOverlay, Portal } from '../../../components'
import { MOBILE_VIEWPORT } from '../../../guided-toolbox/utils/breakpoints'
import { DEFAULT_RETIREMENT_INCOME_GOAL_PCT } from '../../../models/Person'
import { withRouterProps } from '../../../utils'
import IncomeReplacementDrawer from '../../goals/IncomeReplacement/IncomeReplacementDrawer'
import {
  ChangeGoalButton,
  Footer,
  GoalFooterBar,
  SaveError,
  SaveGoalButton,
} from './IncomeGoalStyle'

const SAVE_FAILED = "We couldn't save your goal, please try again."
const FINISH_FAILED =
  "Your goal was saved, but we couldn't finish setting you up, please try again."

const IncomeGoalDecision = ({ navigate, onboarding }) => {
  const isMobile = useMediaQuery(MOBILE_VIEWPORT)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const { person, updatePerson, updateOnBoardingState } = onboarding
  const goalPercent = person?.retirementIncomeGoalPct ?? DEFAULT_RETIREMENT_INCOME_GOAL_PCT

  const toggleDrawer = () => setDrawerOpen(open => !open)

  // Save goal changes from the drawer without advancing onboarding; only the footer completes the flow.
  const saveGoal = async percentOfIncome => {
    setDrawerOpen(false)
    setSaving(true)
    setError(null)

    try {
      await updatePerson({ id: person.id, retirementIncomeGoalPct: percentOfIncome })
    } catch (err) {
      console.error(err)
      setError(SAVE_FAILED)
    } finally {
      setSaving(false)
    }
  }

  // Keep the failures separate because the goal may save even if onboarding completion fails.
  const finish = async () => {
    setSubmitting(true)
    setError(null)

    try {
      await updatePerson({ id: person.id, retirementIncomeGoalPct: goalPercent })
    } catch (err) {
      console.error(err)
      setError(SAVE_FAILED)
      setSubmitting(false)
      return
    }

    try {
      await updateOnBoardingState(true)
    } catch (err) {
      console.error(err)
      setError(FINISH_FAILED)
      setSubmitting(false)
      return
    }

    navigate('/home')
  }

  return (
    <>
      {/* Portal the footer so page animations don't affect its fixed positioning. */}
      <Portal>
        <Footer>
          {error && <SaveError role='alert'>{error}</SaveError>}

          <GoalFooterBar>
            <ChangeGoalButton type='button' disabled={submitting || saving} onClick={toggleDrawer}>
              {isMobile ? 'Change' : 'Change goal'}
            </ChangeGoalButton>
            <SaveGoalButton type='button' disabled={submitting || saving} onClick={finish}>
              Save &amp; calculate
            </SaveGoalButton>
          </GoalFooterBar>
        </Footer>
      </Portal>

      <IncomeReplacementDrawer
        active={drawerOpen}
        toggleDrawer={toggleDrawer}
        handleSave={saveGoal}
        source={onboarding}
      />

      {saving && <LoadingOverlay label='Saving your goal' />}
    </>
  )
}

export default withRouterProps(inject('onboarding')(observer(IncomeGoalDecision)))
